/**
 * Pure helpers for the Door times page (/stores/door-times): 12-hour clock
 * formatting, the "Late by" tone, and range checks. No React, so vitest can
 * load it directly.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Longest range the API serves. */
export const DOOR_TIMES_MAX_DAYS = 92;

/** "10:53:07" -> "10:53 AM". Null or unparseable input yields "--". */
export function formatClock12(t: string | null | undefined): string {
  if (!t) return '--';
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return '--';
  const h = Number(m[1]);
  const min = m[2];
  if (h > 23) return '--';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min} ${suffix}`;
}

/** "2026-10-12" + weekday 1 -> "Mon, Oct 12, 2026". */
export function formatDayLabel(date: string, weekday: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${WEEKDAYS[weekday] ?? ''}, ${month} ${Number(m[3])}, ${m[1]}`;
}

export type LateTone = 'none' | 'amber' | 'red';

/** Amber when the first entry came after open, red when more than 30 minutes after. */
export function lateTone(minutesLate: number | null | undefined): LateTone {
  if (minutesLate === null || minutesLate === undefined || minutesLate <= 0) return 'none';
  return minutesLate > 30 ? 'red' : 'amber';
}

/** Text for the "Late by" cell. */
export function formatLateBy(minutesLate: number | null | undefined): string {
  if (minutesLate === null || minutesLate === undefined) return '--';
  if (minutesLate <= 0) return 'On time';
  return `${minutesLate} min`;
}

export type StatusKind = 'late_open' | 'closed_early' | 'on_time' | 'no_entries' | 'close_pending';

type StatusInput = {
  is_today: boolean;
  entries: number;
  opened_late: boolean;
  closed_early: boolean;
};

/**
 * The Status pills for a row, in display order. A row can carry both
 * "Late open" and "Closed early". Otherwise exactly one pill: "On time" for a
 * finished day with data, "Open, close pending" for today with data, and
 * "No entries" when there is no data.
 */
export function statusKinds(r: StatusInput): StatusKind[] {
  const kinds: StatusKind[] = [];
  if (r.opened_late) kinds.push('late_open');
  if (r.closed_early) kinds.push('closed_early');
  if (kinds.length > 0) return kinds;
  if (r.entries === 0) return ['no_entries'];
  return [r.is_today ? 'close_pending' : 'on_time'];
}

export const STATUS_LABEL: Record<StatusKind, string> = {
  late_open: 'Late open',
  closed_early: 'Closed early',
  on_time: 'On time',
  no_entries: 'No entries',
  close_pending: 'Open, close pending',
};

export function isProblem(r: { opened_late: boolean; closed_early: boolean }): boolean {
  return r.opened_late || r.closed_early;
}

/** One-line range summary. Store-days are rows that have door data or a live late open. */
export function summarize(rows: Array<StatusInput>): { lateOpens: number; earlyCloses: number; storeDays: number } {
  let lateOpens = 0;
  let earlyCloses = 0;
  let storeDays = 0;
  for (const r of rows) {
    if (r.opened_late) lateOpens += 1;
    if (r.closed_early) earlyCloses += 1;
    if (r.entries > 0 || r.opened_late) storeDays += 1;
  }
  return { lateOpens, earlyCloses, storeDays };
}

/** Whole days from `from` to `to`, counting both ends. */
export function daysInclusive(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** The date `days` before `date` (YYYY-MM-DD). */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A message when the range cannot be served, otherwise null. */
export function rangeProblem(from: string, to: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Pick a from date and a to date.';
  if (from > to) return 'The from date is after the to date.';
  if (daysInclusive(from, to) > DOOR_TIMES_MAX_DAYS) return `Pick a range of ${DOOR_TIMES_MAX_DAYS} days or fewer.`;
  return null;
}
